import BenchlingConfig from 'BenchlingConfig';
import type {
  ParameterStructuredTable,
  ParameterStructuredTableRowMetadata,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {ParameterStructuredTableColumnUuid} from 'notebook/integrations/procedures/ParameterStructuredTableColumnInfo';
import {ParameterStructuredTableMode} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';

import {columnIndexByUuid, readCellLabelText, TransposePivotSpec} from './TransposePivot';

/**
 * The `TransposePivotSpec`s for procedure parameter confirmation and measurement tables. This is the ONLY
 * place that knows the parameter-table domain: which columns hold the parameter name, the value to
 * confirm or record, and the condition-replicate identity, and how a canonical row's (parameter, PMEI)
 * identity maps onto the transposed grid (parameters -> rows, condition x replicate "PMEIs" -> columns).
 * Both table types share these columns, so they pivot the same way.
 */

const NAME_COLUMN_UUID = ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID;
const VALUE_COLUMN_UUID = ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID;
const CONDITION_REPLICATE_COLUMN_UUID = ParameterStructuredTableColumnUuid.CONDITION_REPLICATE_COLUMN_UUID;

/** Column key for the degenerate case with no condition-replicate (single implicit batch). */
const SINGLE_BATCH_KEY = '__single__';

/** The header label for a PMEI column, e.g. "Condition A · Replicate 1". */
function pmeiColumnLabel(conditionName: string, replicateLabel: string): string {
  const parts = [conditionName, replicateLabel].filter((part) => part !== '');
  return parts.length > 0 ? parts.join(' · ') : 'Value';
}

/**
 * Run-stable column key for a condition-replicate: `conditionName::replicateNumber`. Used instead of the
 * per-run PMEI so a column keeps its identity — and its persisted width — across worksheets running the
 * same unit operation.
 */
function conditionReplicateColumnKey(conditionName: string, replicateNumber: number | undefined): string {
  return `${conditionName}::${replicateNumber ?? ''}`;
}

function makeParameterTablePivotSpec(mode: ParameterStructuredTableMode): TransposePivotSpec {
  return {
    projectedColumnUuid: VALUE_COLUMN_UUID,
    rowAxisLabel: 'Parameter',
    columnAxisLabel: 'Condition and replicate',
    // The transposed pivot-mode grid skips crash-prone primary-sample logic.
    hasPrimarySamples: false,
    // Persist widths per unit operation (template PMDV) and table mode, since a unit operation's
    // confirmation and measurement tables have the same columns. Every row shares one PMDV (all PSEIs have
    // identical parameters), so the first row that carries it is representative; null falls back to a
    // per-table key.
    getColumnWidthStoreKey: (table: Table) => {
      for (const rowData of table.rowData ?? []) {
        const rowMeta = rowData?.rowMetadata as ParameterStructuredTableRowMetadata | undefined;
        if (rowMeta?.templateMethodDefinitionVersionId != null) {
          return `${mode}::${rowMeta.templateMethodDefinitionVersionId}`;
        }
      }
      return null;
    },
    createRowClassifier: (table: Table) => {
      const colIndexByUuid = columnIndexByUuid(table);
      const nameColIndex = colIndexByUuid.get(NAME_COLUMN_UUID);
      const condRepColIndex = colIndexByUuid.get(CONDITION_REPLICATE_COLUMN_UUID);

      return (rowIndex: number) => {
        const rowMeta = table.rowData?.[rowIndex]?.rowMetadata as
          | ParameterStructuredTableRowMetadata
          | undefined;

        // Row axis: one grid row per parameter.
        const rowKey = rowMeta?.parameterId ?? `row-${rowIndex}`;
        const rowLabel = nameColIndex == null ? '' : readCellLabelText(table.table[rowIndex]?.[nameColIndex]);

        // Column axis: one grid column per condition x replicate. A row's PMEI signals whether it is a
        // condition-replicate row at all; the column is then keyed run-stably (not by the per-run PMEI).
        const pmei = rowMeta?.methodExecutionInstanceId;
        if (pmei == null || condRepColIndex == null) {
          return {rowKey, rowLabel, columnKey: SINGLE_BATCH_KEY, columnLabel: pmeiColumnLabel('', '')};
        }

        const typedValue = table.table[rowIndex]?.[condRepColIndex]?.typedValue;
        // A PMEI row carries exactly one condition-replicate; guard the collection so a malformed or
        // partially hydrated (empty) list falls back to the single-batch column rather than throwing.
        const conditionReplicate =
          typedValue?.type === BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES &&
          typedValue.conditionReplicates.length > 0
            ? typedValue.conditionReplicates[0]
            : undefined;
        const conditionName = conditionReplicate?.conditionName ?? '';
        const replicateLabel =
          conditionReplicate?.replicateNumber != null
            ? `Replicate ${conditionReplicate.replicateNumber}`
            : '';

        return {
          rowKey,
          rowLabel,
          columnKey: conditionReplicateColumnKey(conditionName, conditionReplicate?.replicateNumber),
          columnLabel: pmeiColumnLabel(conditionName, replicateLabel),
        };
      };
    },
  };
}

export const PARAMETER_CONFIRMATION_PIVOT_SPEC = makeParameterTablePivotSpec(
  ParameterStructuredTableMode.PARAMETER_CONFIRMATION
);
export const PARAMETER_MEASUREMENT_PIVOT_SPEC = makeParameterTablePivotSpec(
  ParameterStructuredTableMode.PARAMETER_ACTUAL
);

const PIVOT_SPEC_BY_MODE: Partial<Record<ParameterStructuredTableMode, TransposePivotSpec>> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: PARAMETER_CONFIRMATION_PIVOT_SPEC,
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: PARAMETER_MEASUREMENT_PIVOT_SPEC,
};

/** Whether tables of `mode` can offer Pivot. */
export function isPivotTableMode(mode: ParameterStructuredTableMode): boolean {
  return PIVOT_SPEC_BY_MODE[mode] != null;
}

/**
 * The pivot spec for `structuredTable`, or null when Pivot isn't offered for it. Gated by
 * `PROCEDURES_ENABLE_CONFIRMATION_PIVOT_MODE`. A parameter confirmation or measurement table pivots only
 * when it has the name and value columns, which label the pivot's rows and fill its cells.
 */
export function getParameterTablePivotSpec(
  structuredTable: ParameterStructuredTable
): TransposePivotSpec | null {
  if (!BenchlingConfig.PROCEDURES_ENABLE_CONFIRMATION_PIVOT_MODE) {
    return null;
  }
  const pivotSpec = PIVOT_SPEC_BY_MODE[structuredTable.metadata.mode];
  if (pivotSpec == null) {
    return null;
  }
  const columnUuids = new Set(structuredTable.columns.map((column) => column.provenanceUuid));
  const canPivot = columnUuids.has(NAME_COLUMN_UUID) && columnUuids.has(VALUE_COLUMN_UUID);
  return canPivot ? pivotSpec : null;
}

export const PIVOT_FORMULA_TOOLTIP = "Pivot isn't available for tables containing formulas.";

/**
 * Whether Pivot is unavailable because the table has formula cells. The modal evaluates only this
 * table, so a formula referencing another table would show a value frozen at open time.
 */
export function hasFormulaCells(structuredTable: ParameterStructuredTable): boolean {
  return structuredTable.rows.some((row) => row.cells.some((cell) => TableUtil.isCellFormula(cell)));
}
